import { jwtDecode } from 'jwt-decode';

/**
 * WHO IS THIS SESSION, REALLY — for gating the developer-only modules.
 *
 * The API decides authorization from the JWT's role claims, so the web reads the SAME
 * source first: the role claim(s) of the token that every request actually carries
 * (`sessionStorage.impersonateToken` in a developer "Launch" tab, else
 * `localStorage.authToken`). The strings the login screen stored (`userRoles` /
 * `impersonateRoles`) are never used to decide developer access: they are editable and can be stale.
 *
 * Inside an impersonation tab the effective token is the STORE's, so the developer is
 * deliberately NOT a developer there: the API would refuse the developer endpoints for
 * that token anyway, and the tab exists to see exactly what the store sees.
 *
 * This is a UI gate only. The real enforcement is `[Authorize(Roles = "Developer")]` on
 * the API; this keeps a store user from landing on a page full of 403s.
 */
const ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

export const DEVELOPER_ROLE = 'Developer';

export function getEffectiveToken(): string | null {
  try {
    return sessionStorage.getItem('impersonateToken') || localStorage.getItem('authToken') || null;
  } catch {
    return null;
  }
}

function asRoleList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  if (typeof value === 'string') return value.split(',').map((v) => v.trim()).filter(Boolean);
  return [];
}

/**
 * The token's role claim values VERBATIM — one claim per role, never split or trimmed — because
 * the API matches them ordinally. (Trimming made a role named " Developer" look like Developer.)
 */
function rawTokenRoles(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v)).filter((v) => v.length > 0);
  if (typeof value === 'string' && value.length > 0) return [value];
  return [];
}

/** Role names carried by a JWT (ASP.NET Core emits one ClaimTypes.Role claim per role). */
export function rolesFromToken(token: string | null | undefined): string[] {
  if (!token) return [];
  try {
    const decoded = jwtDecode<Record<string, unknown>>(token);
    return [
      ...rawTokenRoles(decoded[ROLE_CLAIM]),
      ...rawTokenRoles(decoded.role),
      ...rawTokenRoles(decoded.roles),
    ];
  } catch {
    return [];
  }
}

/** Roles stored by the login screen for this tab (impersonation first, like the sidebar). */
export function rolesFromStorage(): string[] {
  try {
    const raw = sessionStorage.getItem('impersonateRoles') ?? localStorage.getItem('userRoles');
    return asRoleList(raw);
  } catch {
    return [];
  }
}

/** The roles the current requests are made with: the token's claims, else the stored list. */
export function getEffectiveRoles(): string[] {
  const fromToken = rolesFromToken(getEffectiveToken());
  return fromToken.length > 0 ? fromToken : rolesFromStorage();
}

/**
 * EXACT match, like the API's [Authorize(Roles = "Developer")] (ordinal). A trimmed or
 * case-folded compare would accept a role named " Developer" or "developer" that the API
 * refuses — the page would open and then fail on every request.
 */
export function rolesIncludeDeveloper(roles: ReadonlyArray<string>): boolean {
  return roles.some((r) => r === DEVELOPER_ROLE);
}

/**
 * True only for a session whose EFFECTIVE token carries the Developer role. Deliberately no
 * fallback to the role list saved in browser storage: that list is editable and can be stale.
 */
export function isDeveloperSession(): boolean {
  return rolesIncludeDeveloper(rolesFromToken(getEffectiveToken()));
}
